import { ReactComponent as AudioIcon } from "../../assets/icons/audio/audio-icon.svg";
import { ReactComponent as SpeechIcon } from "../../assets/icons/audio/speech-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/Common/trash-icon.svg";

import "./index.css";
import { EInteractiveAudioType, LanguageType } from "../../models/IInteractiveAudio";
import { SecondaryLanguageSwitch } from "../../components/NarrationDrawer/components/SecondaryLanguageSwitch";
import { EffectAudioState, NarrationAudioState, OneShotAudioState } from "../../contexts/PageAudioManager/types";
import MachineTranslation from "../../components/Modals/CognitiveServices/MachineTranslation/MachineTranslation";
import { useState } from "react";
import { ReactComponent as TranslateIcon } from "../../assets/icons/NarratorTab/translate-icon.svg";
import { AbstractTooltip as Tooltip } from "../../components/ToolTip/ToolTip";
import { applyTranslationSave } from "../../components/Modals/CognitiveServices/MachineTranslation/translationSave";
import { useAudioManagerStore } from "../../contexts/PageAudioManager";
import { useLessonPage } from "../../contexts/LessonPagesProvider/LessonPagesProvider";

interface PageManifestWithNarration {
  narrationAudios?: NarrationAudioState[];
}

export interface AudioActionsProps {
  audio: OneShotAudioState | NarrationAudioState | EffectAudioState;
  hasSecondaryAudio: boolean;
  onUpload: () => void;
  onTextToSpeech: () => void;
  onRemove: () => void;
  onChangeLanguage: (language: LanguageType) => void;
  onCreateSecondary?: () => void;
  type: EInteractiveAudioType;
  pageManifest?: PageManifestWithNarration;
  addButtonWrapper?: (button: React.ReactNode) => React.ReactNode;
}
const AUDIO_ACTION_LABELS = {
  EFFECT: "Remove Audio",
  NARRATION: "Replace or Remove Audio",
} as const;

export const AudioActions = ({
  audio,
  hasSecondaryAudio,
  onUpload,
  onTextToSpeech,
  onRemove,
  onChangeLanguage,
  onCreateSecondary,
  type,
  pageManifest,
  addButtonWrapper,
}: AudioActionsProps) => {
  const { updateManifest, pageManifest: contextPageManifest } = useLessonPage();
  const updateAudio = useAudioManagerStore((state) => state.updateAudio);
  const label = type === EInteractiveAudioType.EFFECT ? AUDIO_ACTION_LABELS.EFFECT : AUDIO_ACTION_LABELS.NARRATION;
  const narrationIndex =
    pageManifest?.narrationAudios?.findIndex((a: NarrationAudioState) => a.objectId === audio.objectId) ?? -1;
  const audioIndexForLegacy = Math.max(0, narrationIndex);
  const alternate = audio.language === LanguageType.SECONDARY || !!audio.parentObjectId;

  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState(false);

  return (
    <>
      {type === EInteractiveAudioType.NARRATION ? (
        <div className="audio-actions-wrapper">
          <SecondaryLanguageSwitch
            activeLanguage={audio.language as LanguageType}
            onSelectPrimary={() => onChangeLanguage(LanguageType.PRIMARY)}
            onSelectSecondary={() => onChangeLanguage(LanguageType.SECONDARY)}
            onCreateSecondary={() => onCreateSecondary?.()}
            hasSecondary={hasSecondaryAudio}
            addButtonWrapper={addButtonWrapper}
          />
          <div className="audio-actions-translate">
            <button
              className="translation-modal-button"
              data-tooltip-id="Translations"
              onClick={() => {
                setIsTranslationModalOpen(true);
              }}
            >
              <TranslateIcon />
            </button>
            <Tooltip id={"Translations"} />
            <MachineTranslation
              initialText={audio.savedText as string}
              open={isTranslationModalOpen}
              onClose={(translatedText: string | null) => {
                setIsTranslationModalOpen(false);
                if (!translatedText) {
                  return;
                }

                const manifestToUpdate = pageManifest ?? contextPageManifest;
                if (!manifestToUpdate) {
                  return;
                }

                const newManifest = applyTranslationSave({
                  pageManifest: manifestToUpdate,
                  translatedText,
                  toDirection: undefined, // direction not provided by new modal; keep existing
                  audioIndex: audioIndexForLegacy,
                  isAlternate: alternate,
                  isAllModes: false,
                  narrationIndex: narrationIndex >= 0 ? narrationIndex : undefined,
                  narrationObjectId: (audio as NarrationAudioState).objectId,
                  updateAudio,
                });

                updateManifest(newManifest);
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="audio-actions-title">{label}</div>
      <div className="audio-actions-wrapper">
        {type === EInteractiveAudioType.NARRATION ? (
          <>
            <div className="audio-action">
              <button
                className="cpat-icon-button cpat-icon-button--dark cpat-icon-button--xl cpat-icon-button--rounded"
                title="Upload Audio"
                onClick={onUpload}
              >
                <AudioIcon />
              </button>
              <div className="audio-action-label">Upload Audio</div>
            </div>
            <div className="audio-action">
              <button
                className="cpat-icon-button cpat-icon-button--dark cpat-icon-button--xl cpat-icon-button--rounded"
                title="Text to Speech"
                onClick={onTextToSpeech}
              >
                <SpeechIcon />
              </button>
              <div className="audio-action-label">Text to Speech</div>
            </div>
          </>
        ) : null}

        <div className="audio-action">
          <button
            className="cpat-icon-button cpat-icon-button--dark cpat-icon-button--xl cpat-icon-button--rounded"
            title="Remove Audio"
            onClick={onRemove}
          >
            <DeleteIcon />
          </button>
          <div className="audio-action-label">Remove Audio</div>
        </div>
      </div>
    </>
  );
};
